import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { rpc, supabase } from './supabase';
import type { SessionStatus } from './types';

type Factor = { id: string; status: string; friendly_name?: string };
type Enrollment = { id: string; qr: string } | null;

const FACTOR_NAME = 'CoMiles Admin';

export function AuthGate({ children }: { children: ReactNode }) {
  const [checking, setChecking] = useState(true);
  const [hasSession, setHasSession] = useState(false);
  const [status, setStatus] = useState<SessionStatus | null>(null);
  const [factor, setFactor] = useState<Factor | null>(null);
  const [stale, setStale] = useState<Factor | null>(null);
  const [enrollment, setEnrollment] = useState<Enrollment>(null);
  const [code, setCode] = useState('');
  const [message, setMessage] = useState('');
  const [checkError, setCheckError] = useState('');
  const [working, setWorking] = useState(false);

  const inspect = useCallback(async () => {
    setChecking(true);
    setMessage('');
    setCheckError('');
    const { data } = await supabase.auth.getSession();
    setHasSession(Boolean(data.session));
    if (!data.session) {
      setStatus(null);
      setChecking(false);
      return;
    }
    try {
      const next = await rpc<SessionStatus>('admin_session_status');
      setStatus(next);
      if (next.role === 'admin' && !next.adminAccess) {
        // `data.totp` lists verified factors only; an abandoned enrollment
        // lives in `data.all` and would otherwise be invisible here — and
        // invisible is what makes re-enrolling fail on the friendly name.
        const factors = await supabase.auth.mfa.listFactors();
        const totp = (factors.data?.all ?? []).filter((item) => item.factor_type === 'totp');
        setFactor(totp.find((item) => item.status === 'verified') ?? null);
        setStale(totp.find((item) => item.status === 'unverified') ?? null);
      }
    } catch (error) {
      // A timed-out or failed status check is not an authorization decision.
      // Keep `status` untouched so the render below offers a retry instead of
      // telling a genuine admin they have no role.
      setCheckError(error instanceof Error ? error.message : 'Unable to verify dashboard access');
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    void inspect();
    const { data } = supabase.auth.onAuthStateChange(() => { window.setTimeout(() => void inspect(), 0); });
    return () => data.subscription.unsubscribe();
  }, [inspect]);

  async function login() {
    setWorking(true);
    setMessage('');
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin, skipBrowserRedirect: false },
    });
    if (error) {
      setMessage(error.message);
      setWorking(false);
    }
  }

  async function beginEnrollment() {
    setWorking(true);
    setMessage('');
    // An enrollment abandoned before verification leaves an unverified factor
    // holding the friendly name, and enrolling again under that name is
    // rejected. The factor was never usable as a second factor, so discarding
    // it weakens nothing — and not discarding it locks the sole admin out.
    if (stale) {
      const { error: unenrollError } = await supabase.auth.mfa.unenroll({ factorId: stale.id });
      if (unenrollError) {
        setMessage(`Could not clear the previous incomplete enrollment: ${unenrollError.message}`);
        setWorking(false);
        return;
      }
      setStale(null);
    }
    const { data, error } = await supabase.auth.mfa.enroll({
      factorType: 'totp',
      friendlyName: FACTOR_NAME,
    });
    if (error) setMessage(error.message);
    else if (data) {
      setFactor({ id: data.id, status: 'unverified' });
      setEnrollment({ id: data.id, qr: data.totp.qr_code });
    }
    setWorking(false);
  }

  async function verify() {
    if (!factor || code.trim().length !== 6) {
      setMessage('Enter the 6-digit code from your authenticator app.');
      return;
    }
    setWorking(true);
    setMessage('');
    const { error } = await supabase.auth.mfa.challengeAndVerify({
      factorId: factor.id,
      code: code.trim(),
    });
    if (error) {
      setMessage(error.message);
      setWorking(false);
      return;
    }
    await supabase.auth.refreshSession();
    setEnrollment(null);
    setCode('');
    setWorking(false);
    await inspect();
  }

  if (checking) {
    return <main className="auth-shell"><div className="auth-card"><div className="brand-mark">C</div><h1>Verifying access</h1><div className="auth-loader" /></div></main>;
  }

  if (!hasSession) {
    return (
      <main className="auth-shell">
        <section className="auth-card">
          <div className="brand-lockup"><div className="brand-mark">C</div><div><strong>CoMiles</strong><span>Operations Console</span></div></div>
          <div className="auth-copy">
            <span className="eyebrow">Restricted access</span>
            <h1>Run the commute network with clarity.</h1>
            <p>Sign in with the identity assigned the CoMiles administrator role. Access is verified again by the database.</p>
          </div>
          <div className="auth-actions">
            <button className="provider-button" disabled={working} onClick={() => void login()}>Continue with Google</button>
          </div>
          {message && <p className="inline-error" role="alert">{message}</p>}
        </section>
      </main>
    );
  }

  if (checkError) {
    return (
      <main className="auth-shell"><section className="auth-card compact">
        <div className="brand-mark">C</div><span className="eyebrow">Check failed</span>
        <h1>Couldn’t confirm this session.</h1>
        <p>Your sign-in is intact — the access check itself did not complete. This is usually a network or database timeout, not a permissions problem.</p>
        <p className="inline-error" role="alert">{checkError}</p>
        <button className="primary-button" onClick={() => void inspect()}>Try again</button>
        <button className="text-button" onClick={() => void supabase.auth.signOut()}>Sign out</button>
      </section></main>
    );
  }

  // `support_roles.role` is one of support_agent / safety_lead / admin. Only
  // admin can ever satisfy `adminAccess`, so anything else must be denied here
  // rather than falling through to an MFA step it can never clear.
  if (status?.role !== 'admin') {
    return (
      <main className="auth-shell"><section className="auth-card compact">
        <div className="brand-mark">C</div><span className="eyebrow">Access denied</span>
        <h1>This account is not an administrator.</h1>
        <p>{status?.role
          ? `This identity holds the ${status.role.replaceAll('_', ' ')} role. The operations console is limited to the administrator role.`
          : 'Your identity is valid, but no CoMiles admin role is assigned to it.'}</p>
        <code className="user-id">{status?.userId}</code>
        <button className="primary-button" onClick={() => void supabase.auth.signOut()}>Sign out</button>
      </section></main>
    );
  }

  if (!status.adminAccess) {
    return (
      <main className="auth-shell"><section className="auth-card compact">
        <div className="brand-mark">C</div><span className="eyebrow">Second factor required</span>
        <h1>Secure this admin session.</h1>
        <p>{factor ? 'Enter the current code from your authenticator app.' : 'Enroll an authenticator before opening production operations.'}</p>
        {!factor && <button className="primary-button" disabled={working} onClick={() => void beginEnrollment()}>Set up authenticator</button>}
        {enrollment && <div className="qr-wrap"><img src={enrollment.qr} alt="Authenticator enrollment QR code" /><small>Scan in 1Password, Google Authenticator, Authy, or another TOTP app.</small></div>}
        {factor && <div className="mfa-form">
          <label htmlFor="mfa-code">6-digit security code</label>
          <input id="mfa-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode="numeric" autoComplete="one-time-code" placeholder="000000" />
          <button className="primary-button" disabled={working || code.length !== 6} onClick={() => void verify()}>Verify and continue</button>
        </div>}
        {message && <p className="inline-error" role="alert">{message}</p>}
        <button className="text-button" onClick={() => void supabase.auth.signOut()}>Use a different account</button>
      </section></main>
    );
  }

  return <>{children}</>;
}

